import React from 'react';
import { SelectSearchOption } from 'react-select-search';
import reorderHelper from '../../../infrastructure/helpers/common/reorderHelper';
import sortingHelper from '../../../infrastructure/helpers/common/sortingHelper';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import conditionDisplayHelper from '../../../infrastructure/helpers/functions/condition/conditionDisplayHelper';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import Condition from '../../../types/functions/Condition/Condition';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import ActionType from '../../../types/report/ActionType';
import AggregationFilter from '../../../types/report/AggregationFilter';
import ComponentType from '../../../types/report/ComponentType';
import DragAndDropItem from '../../../types/report/DragAndDropItem';
import IdNameAndNumber from '../../../types/report/IdNameAndNumber';
import ErrorList from '../../Common/ErrorList/ErrorList';
import InsertAtPositionModal from '../../Common/Modals/InsertAtPositionModal/InsertAtPositionModal';
import BespokeConditionBuilder from '../../Functions/Bespoke/BespokeConditionBuilder';
import ConditionBuilder from '../../Functions/Condition/ConditionBuilder';
import StatementBuilderProps from '../../Functions/Statement/StatementBuilderProps';
import reportComponentBuildersHelper from '../Common/ReportComponentsHelper';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import { getColumns } from './AggregationFilterBuilder.Grid';
import InfoIcon from '../../Common/InfoIcon/InfoIcon';
import guidance from '../ReportConfigEditor/ReportConfigEditor.Guidance';
import Aggregation from '../../../types/report/Aggregation';

class AggregationFilterBuilder extends React.Component<StatementBuilderProps<AggregationFilter>, { showPopup: boolean }> {
    constructor(props: StatementBuilderProps<AggregationFilter>) {
        super(props);

        this.state = {
            showPopup: false
        }

        this.onNameChange = this.onNameChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onAggregationChange = this.onAggregationChange.bind(this);
        this.onBespokeToggleClick = this.onBespokeToggleClick.bind(this);
        this.getConditionContent = this.getConditionContent.bind(this);
        this.reorder = this.reorder.bind(this);
        this.onDropBetweenFilters = this.onDropBetweenFilters.bind(this);
        this.onFilterConfiguredInPopup = this.onFilterConfiguredInPopup.bind(this);
        this.onAddFilterClick = this.onAddFilterClick.bind(this);
        this.validateInsertBeforeAfter = this.validateInsertBeforeAfter.bind(this);
    }

    onNameChange(name: string, number: number): void {

        this.props.onStatementChange({
            number: number,
            name: name
        } as AggregationFilter);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        this.props.onStatementChange({
            number: number,
            businessDescription: businessDescription
        } as AggregationFilter);
    }

    onAggregationChange(aggregationNumber: number, filterNumber: number): void {

        const aggregation = this.props.reportConfig.aggregations.find(a => a.number === aggregationNumber) as Aggregation;

        const name = `${aggregation.name} ${this.props.details.name}-${filterNumber}`;

        this.props.onStatementChange({
            number: filterNumber,
            name: name,
            aggregationNumber: aggregationNumber,
            dataSourceId: aggregation.dataSourceId,
        } as AggregationFilter);
    }

    onBespokeToggleClick(isBespoke: boolean, filter: AggregationFilter): void {

        let changes = { number: filter.number, isBespoke: isBespoke } as AggregationFilter;

        if (isBespoke) {
            changes.bespokeCondition = conditionDisplayHelper.buildConditions(filter.condition, [], [], [], [], this.props.collections.reportFields, null as any, this.props.reportConfig.type);
        }

        this.props.onStatementChange(changes);
    }

    onReorderConditions(condition: Condition, filter: AggregationFilter) {

        filter.condition = condition;
        this.props.onStatementChange(filter);
    }

    async onClearClick(condition: Condition, filter: AggregationFilter): Promise<void> {

        let newFilter = assignmentHelper.clearOperandsInStatementByConditionNumber(condition.number, filter) as AggregationFilter;

        this.props.onStatementChange(newFilter);
    }

    clearAllErrors() {
        this.props.statements.forEach((filter) => {
            filter.errors = [];
        });
    }

    async reorder(dragNumber: number, dropNumber: number): Promise<void> {

        let dragIndex = this.props.statements.findIndex(x => x.number === dragNumber);
        let dropIndex = this.props.statements.findIndex(x => x.number === dropNumber);

        let draggedFilter = this.props.statements.find(v => v.number === dragNumber) as AggregationFilter;
        let dropFilter = this.props.statements.find(v => v.number === dropNumber) as AggregationFilter;

        this.clearAllErrors();

        let result = reportComponentBuildersHelper.validateDragAndDrop(draggedFilter as DragAndDropItem, dropFilter as DragAndDropItem, ComponentType.AggregationFilter);

        if (result.error) {
            const filterReferenced = this.props.statements.find(s => s.number === result.number) || {} as AggregationFilter;

            result.error = `${result.error} The referenced filter is "${filterReferenced.name}".`;

            const number = result.isDrag ? draggedFilter.number : dropFilter.number;

            const statement = this.props.statements.find(s => s.number === number) || {} as AggregationFilter;

            statement.errors = [result.error];

            this.props.onStatementChange(statement);

            return;
        }
        else {

            reorderHelper.reorderWithNumbersChange(this.props.statements, dragIndex, dropIndex);

            this.props.onDragAndDrop(this.props.statements);
        }
    }

    onDropBetweenFilters(dragNumber: number, dropNumber: number) {

        let numbersMap = reorderHelper.prepareNewNumbersMapOnDragBetween(dragNumber, dropNumber);

        this.props.onDragStatementBetween && this.props.onDragStatementBetween(numbersMap);
    }

    getLocation(filter: AggregationFilter): ReportLogicLocation {

        let location = {} as ReportLogicLocation;

        location.statement = this.props.details.statement;
        location.statementNumber = filter.number;
        location.parentNumber = filter.aggregationNumber;

        return location;
    }

    getConditionContent(filter: AggregationFilter): JSX.Element {

        if (filter.isBespoke) {
            return (
                <BespokeConditionBuilder
                    keyword={this.props.details.keyword}
                    keyword2={this.props.details.keyword2}
                    statement={filter}
                    onChange={this.props.onStatementChange}
                    isReadOnly={this.props.isReadOnly} />
            );
        }

        return (
            <>
                <ConditionBuilder
                    reportConfigType={this.props.reportConfig.type}
                    isReadOnly={this.props.isReadOnly}
                    allowCopy={true}
                    keyword={this.props.details.keyword}
                    functions={this.props.reportConfig.userDefinedFunctions}
                    globalFunctions={this.props.collections.globalModuleConfig.userDefinedFunctions}
                    lookups={this.props.reportConfig.lookups}
                    dictionaries={this.props.reportConfig.dictionaries}
                    variables={this.props.reportConfig.variables}
                    aggregations={this.props.reportConfig.aggregations}
                    dataSources={this.props.collections.dataSources}
                    reportFields={this.props.collections.reportFields}
                    customDataSourceFields={null as any}
                    location={this.getLocation(filter)}
                    condition={filter.condition}
                    onLogicalOperatorChange={this.props.onLogicalOperatorChange}
                    onParenthesesChange={this.props.onParenthesesChange}
                    onAddConditionClick={this.props.onAddConditionClick}
                    onRemoveConditionClick={(location, keyword, event) => this.props.onRemoveConditionClick(location, event)}
                    onComparisonTypeChange={this.props.onComparisonTypeChange}
                    onOperandClick={this.props.onOperandClick}
                    onCopyClick={this.props.onCopyClick}
                    onPasteClick={this.props.onPasteClick}
                    allowPaste={this.props.allowPaste}
                    onClearClick={async (e, condition) => { this.onClearClick(condition, filter); }}
                    onReorderConditions={(condition) => { this.onReorderConditions(condition, filter); }} />

                <div className="code-block">
                    <span className="code">{this.props.details.keyword2}</span>
                </div>

                <ErrorList errors={filter.errors} className="mt-2" />
            </>
        );
    }

    onAddFilterClick(): void {

        let filtersLength = this.props.statements.length;

        if (filtersLength > 0) {
            this.setState({ showPopup: true });
        }
        else {
            const aggregation = this.props.reportConfig.aggregations[0];
            const name = `${aggregation.name} ${this.props.details.name}-${filtersLength + 1}`;

            this.props.onAddStatementClick(name, (f) => {
                f.dataSourceId = aggregation.dataSourceId;
                f.aggregationNumber = aggregation.number;
            });
        }
    }

    async onFilterConfiguredInPopup(name: string, type: string, isInsertAfter: boolean, selectedNumber: string): Promise<void> {
        this.setState({ showPopup: false });
        const insertionAfterNumber: number = parseInt(selectedNumber);

        let filtersLength = this.props.statements.length;
        let aggregation = this.props.reportConfig.aggregations[0];

        let finalName = name || `${aggregation?.name} ${this.props.details.name}-${filtersLength + 1}`;

        this.props.onAddStatementClick(finalName, (filter, filters) => {

            filter.dataSourceId = aggregation.dataSourceId;
            filter.aggregationNumber = aggregation.number;

            let newFilters = this.props.statements;

            newFilters = reorderHelper.rearangeElementsWhenAddNewOne(newFilters, filter, isInsertAfter, insertionAfterNumber) as AggregationFilter[];

            if (!name) {
                filter._internalId = filter.number;
            }
            else {
                filter.name = name
            }

            filters.forEach((f) => {
                let el = newFilters.find(x => x.name === f.name);

                f.number = el?.number as number;
            });

            filters = sortingHelper.sortByNumberAsc(filters) as AggregationFilter[];
        });
    }

    prepareOptionsFromFilters(): SelectSearchOption[] {

        let options: SelectSearchOption[] = [];

        this.props.statements.forEach((filter) => {
            options.push({
                name: filter.name,
                value: filter.name,
                items: [{ name: filter.number.toString(), value: filter.number.toString() }]
            });
        });

        return options;
    }

    async validateInsertBeforeAfter(number: number, isInsertAfter: boolean) {

        return await reportComponentBuildersHelper.validateAction(
            number,
            isInsertAfter,
            this.props.statements as IdNameAndNumber[],
            ComponentType.Filter,
            ActionType.Add,
            this.props.reportConfig);
    }

    render(): JSX.Element {

        if (this.props.reportConfig.aggregations.length === 0) {
            return <></>;
        }

        const filterOptions = this.prepareOptionsFromFilters();

        return (
            <>
                <div className="section-heading">
                    <InfoIcon info={guidance.aggregationFilter} />
                    <h4>Post Aggregation Filters</h4>
                </div>

                <ReportLogicList
                    onDropZoneDrop={this.onDropBetweenFilters}
                    isWaiting={this.props.isWaiting}
                    items={this.props.statements}
                    columns={getColumns(this.onNameChange,
                        this.onBusinessDescriptionChange,
                        this.onBespokeToggleClick,
                        this.props.onRemoveStatementClick,
                        this.onAggregationChange,
                        this.reorder,
                        this.props.isReadOnly,
                        ComponentType.AggregationFilter,
                        this.props.reportConfig)}
                    getContent={this.getConditionContent}
                    getKey={f => f.number}
                    createHtmlId={(filter) => reportConfigNavigation.buildAggregationFilterId(filter.number)}/>
                {
                    !this.props.isReadOnly &&
                    !this.props.isWaiting &&
                    <div className="mt-4 mb-4">
                        <button onClick={this.onAddFilterClick} className="btn btn-light">+ {this.props.details.name}</button>
                    </div>
                }
                {
                    this.state.showPopup ?
                        <InsertAtPositionModal
                            title='New Filter'
                            onSaveClick={this.onFilterConfiguredInPopup}
                            name={this.props.details.name}
                            dropdownOptions={filterOptions}
                            onClose={() => { this.setState({ showPopup: false }); }}
                            defaultDropdownOption={filterOptions[0]}
                            defaultDropdownOptionAlwaysLastOne={true}
                            validateFunction={this.validateInsertBeforeAfter}
                        />
                        : null
                }
            </>
        );
    }
}

export default AggregationFilterBuilder;
